import Close from '@mui/icons-material/Close';
import { alertClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { FileDownloadResult_Read, FileDownloadState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { noop, uniq } from 'lodash-es';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { downloadFile } from '~utils/utilFunctions';

const AVAILABLE_DOWNLOADS_TOASTER_AUTO_HIDE_DURATION_IN_MS = 3000;

export enum FileDownloadOutcome {
  SUCCESS = 'success',
  ERROR = 'error',
  TIMEOUT = 'timeout',
}

type EnqueueFileDownloadOptions = {
  // Called once the server has confirmed the channel subscription. A result
  // broadcast before this point is lost, so a caller that can read the result another
  // way does so now.
  onSubscribed?: () => void;
  onSettled?: (outcome: FileDownloadOutcome) => void;
  // A caller that follows up on an error or timeout with its own, more specific
  // message opts out of the generic "download failed" toast.
  shouldSuppressErrorToast?: boolean;
  // Bulk downloads (dialog-driven, no size bound) rely on the default of no
  // timeout and wait indefinitely, same as before this context existed. Callers
  // with a tighter expectation (a single job-card PDF render) opt into one.
  timeoutMs?: number;
};

type FileDownloadResultContextType = {
  // Stops tracking without calling `onSettled`, for a caller that settled the download
  // by other means (e.g. read the result itself).
  cancelFileDownload: (fileDownloadId: string) => void;
  enqueueFileDownload: (
    fileDownloadId: string,
    options?: EnqueueFileDownloadOptions,
  ) => void;
};

type AvailableDownload = {
  fileName: string;
  fileUrl: string;
  id: string;
};

type TrackedFileDownload = Pick<
  EnqueueFileDownloadOptions,
  'onSettled' | 'shouldSuppressErrorToast'
> & {
  subscription?: Subscription;
  timeoutId?: ReturnType<typeof setTimeout>;
};

const FileDownloadResultContext = createContext<FileDownloadResultContextType>({
  cancelFileDownload: noop,
  enqueueFileDownload: noop,
});

function FileDownloadResultContextProvider({ children }: React.PropsWithChildren) {
  const { toasterStore } = useStores();

  const [fileDownloadIds, setFileDownloadIds] = useState<string[]>([]);
  const [pendingFileDownloads, setPendingFileDownloads] = useState<
    FileDownloadResult_Read[]
  >([]);

  const trackedFileDownloadsById = useRef<Record<string, TrackedFileDownload>>({});
  const downloadedFileIds = useRef<Set<string>>(new Set());

  const availableDownloads = useMemo(() => {
    return pendingFileDownloads.reduce((acc, item) => {
      const isSuccess = item.state === FileDownloadState.SUCCESS;
      const id = item.id;
      const fileUrl = item.objects?.[0]?.url;
      const fileName = item.objects?.[0]?.name;

      if (isSuccess && id && fileUrl && fileName) {
        acc.push({ id, fileName, fileUrl });
      }

      return acc;
    }, [] as AvailableDownload[]);
  }, [pendingFileDownloads]);

  const dequeueFileDownload = useCallback(
    (fileDownloadId: string, outcome: FileDownloadOutcome) => {
      setFileDownloadIds((list) => list.filter((id) => id !== fileDownloadId));

      const onSettled = trackedFileDownloadsById.current[fileDownloadId]?.onSettled;
      delete trackedFileDownloadsById.current[fileDownloadId];
      onSettled?.(outcome);
    },
    [],
  );

  const pushFileDownloadErrorToast = useCallback((fileDownloadId: string) => {
    if (trackedFileDownloadsById.current[fileDownloadId]?.shouldSuppressErrorToast) {
      return;
    }

    toasterStore.push(alert(t('common.file_download.error_message'), AlertTypes.error));
  }, []);

  const subscribeToFileDownloadResultChannel = useCallback(
    (fileDownloadId: string, onSubscribed?: () => void) => {
      const consumer = connection.realTimeConnection;
      let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
        return (
          sub.identifier.includes(realTimeChannels.FileDownloadResultChannel) &&
          sub.identifier.includes(fileDownloadId)
        );
      });

      if (!subscription) {
        subscription = consumer?.subscriptions?.create?.(
          {
            channel: realTimeChannels.FileDownloadResultChannel,
            id: fileDownloadId,
          },
          {
            connected() {
              onSubscribed?.();
            },
            received(data: FileDownloadResult_Read) {
              clearTimeout(trackedFileDownloadsById.current[fileDownloadId]?.timeoutId);

              const isError = data.state === FileDownloadState.ERROR;
              const hasDownloadableObject = Boolean(
                data.objects?.[0]?.url && data.objects?.[0]?.name,
              );

              const isFailure = isError || !hasDownloadableObject;

              if (isFailure) {
                pushFileDownloadErrorToast(fileDownloadId);
              } else {
                setPendingFileDownloads((state) => [...state, data]);
              }

              dequeueFileDownload(
                fileDownloadId,
                isFailure ? FileDownloadOutcome.ERROR : FileDownloadOutcome.SUCCESS,
              );
              this.unsubscribe();
            },
          },
        );
      }

      return subscription;
    },
    [dequeueFileDownload, pushFileDownloadErrorToast],
  );

  const enqueueFileDownload = useCallback(
    (fileDownloadId: string, options?: EnqueueFileDownloadOptions) => {
      const trackedFileDownload: TrackedFileDownload = {
        ...trackedFileDownloadsById.current[fileDownloadId],
        onSettled: options?.onSettled,
        shouldSuppressErrorToast: options?.shouldSuppressErrorToast,
      };
      trackedFileDownloadsById.current[fileDownloadId] = trackedFileDownload;
      setFileDownloadIds((list) => uniq([...list, fileDownloadId]));

      if (!trackedFileDownload.subscription) {
        trackedFileDownload.subscription = subscribeToFileDownloadResultChannel(
          fileDownloadId,
          options?.onSubscribed,
        );

        if (options?.timeoutMs) {
          trackedFileDownload.timeoutId = setTimeout(() => {
            trackedFileDownloadsById.current[fileDownloadId]?.subscription?.unsubscribe();

            pushFileDownloadErrorToast(fileDownloadId);
            dequeueFileDownload(fileDownloadId, FileDownloadOutcome.TIMEOUT);
          }, options.timeoutMs);
        }
      }

      toasterStore.push(
        alert(
          <PreparingDownloadsToaster
            onClose={() => {
              toasterStore.hidePermanentAlert();
            }}
          />,
          'info',
          undefined,
          undefined,
          true,
          {
            [`& .${paperClasses.root}`]: {
              boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
              maxWidth: '250px',
              overflow: 'hidden',
              p: 0,
            },
            [`& .${alertClasses.icon}`]: { display: 'none' },
            [`& .${alertClasses.message}`]: { p: 0 },
          },
          { vertical: 'bottom', horizontal: 'right' },
          true,
        ),
        true,
      );
    },
    [
      dequeueFileDownload,
      pushFileDownloadErrorToast,
      subscribeToFileDownloadResultChannel,
    ],
  );

  const cancelFileDownload = useCallback((fileDownloadId: string) => {
    const trackedFileDownload = trackedFileDownloadsById.current[fileDownloadId];
    clearTimeout(trackedFileDownload?.timeoutId);
    trackedFileDownload?.subscription?.unsubscribe();
    delete trackedFileDownloadsById.current[fileDownloadId];
    setFileDownloadIds((list) => list.filter((id) => id !== fileDownloadId));
  }, []);

  useEffect(() => {
    if (!availableDownloads.length) return;

    // Keyed on the whole array, so a shrink (onDownload/onClose) would otherwise
    // re-download whichever entry is now last. Ids are evicted as their entries
    // leave the list, so this never outgrows what is currently on screen.
    const newlyAvailable = availableDownloads.filter(
      (download) => !downloadedFileIds.current.has(download.id),
    );

    newlyAvailable.forEach((download) => {
      downloadFile(download.fileName, download.fileUrl, true);
      downloadedFileIds.current.add(download.id);
    });

    const clearAvailableDownloads = () => {
      downloadedFileIds.current.clear();
      setPendingFileDownloads([]);
    };

    toasterStore.push(
      alert(
        <AvailableDownloadsToaster
          downloads={availableDownloads}
          onClose={clearAvailableDownloads}
          onDownload={(id: string) => {
            downloadedFileIds.current.delete(id);
            setPendingFileDownloads((state) => {
              return state.filter((item) => item.id !== id);
            });
          }}
        />,
        'info',
        AVAILABLE_DOWNLOADS_TOASTER_AUTO_HIDE_DURATION_IN_MS,
        undefined,
        true,
        {
          [`& .${paperClasses.root}`]: {
            boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
            maxWidth: '350px',
            overflow: 'hidden',
            p: 0,
          },
          [`& .${alertClasses.icon}`]: { display: 'none' },
          [`& .${alertClasses.message}`]: { p: 0 },
        },
        { vertical: 'bottom', horizontal: 'right' },
        true,
        clearAvailableDownloads,
      ),
      true,
    );
  }, [availableDownloads]);

  useEffect(() => {
    // An error result never touches availableDownloads, so this must also watch
    // fileDownloadIds or an error leaves the preparing toast stuck on screen.
    if (!fileDownloadIds.length && !availableDownloads.length) {
      toasterStore.hidePermanentAlert();
    }
  }, [fileDownloadIds.length, availableDownloads.length]);

  useEffect(() => {
    const trackedFileDownloads = trackedFileDownloadsById.current;

    return () => {
      Object.values(trackedFileDownloads).forEach(({ subscription, timeoutId }) => {
        clearTimeout(timeoutId);
        subscription?.unsubscribe();
      });
    };
  }, []);

  return (
    <FileDownloadResultContext.Provider
      value={{ cancelFileDownload, enqueueFileDownload }}
    >
      {children}
    </FileDownloadResultContext.Provider>
  );
}

interface PreparingDownloadsToasterProps {
  onClose: () => void;
}

function PreparingDownloadsToaster({ onClose }: PreparingDownloadsToasterProps) {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        bgcolor: theme.semantic.surfaceToast,
        boxShadow: `inset 0 0 0 1px ${theme.semantic.borderToast}`,
        display: 'flex',
        gap: 2,
        p: 2,
      })}
    >
      <LoadingSpinner isVisible sx={{ flex: 0 }} />

      <Typography
        sx={(theme) => ({
          color: theme.semantic.textOnToast,
          fontSize: '12px',
          fontWeight: 600,
          flex: 1,
        })}
      >
        {t('common.file_download.toasters.preparing.primary_text')}
      </Typography>

      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        <Close
          sx={(theme) => ({ fontSize: '13px', color: theme.semantic.textOnToast })}
        />
      </IconButton>
    </Box>
  );
}

interface AvailableDownloadsToasterProps {
  downloads: AvailableDownload[];
  onClose: () => void;
  onDownload: (id: string) => void;
}

function AvailableDownloadsToaster({
  downloads,
  onClose,
  onDownload,
}: AvailableDownloadsToasterProps) {
  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.semantic.surfaceToast,
        boxShadow: `inset 0 0 0 1px ${theme.semantic.borderToast}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      })}
    >
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={(theme) => ({
              color: theme.semantic.textOnToast,
              fontSize: '12px',
              fontWeight: 600,
            })}
          >
            {t('common.file_download.toasters.available.primary_text')}
          </Typography>

          <Typography
            sx={(theme) => ({
              color: theme.semantic.textOnToastSecondary,
              fontSize: '11px',
              fontWeight: 600,
              mt: '2px',
            })}
          >
            {t('common.file_download.toasters.available.secondary_text')}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <Close
            sx={(theme) => ({ fontSize: '13px', color: theme.semantic.textOnToast })}
          />
        </IconButton>
      </Box>

      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {downloads.map((download) => (
          <Typography
            component="a"
            href={download.fileUrl}
            key={download.id}
            onClick={(event) => {
              event.stopPropagation();
              onDownload(download.id);
            }}
            rel="noopener noreferrer"
            target="_blank"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadYellowDark,
              fontSize: '12px',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            })}
          >
            {`• ${download.fileName}`}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export { FileDownloadResultContextProvider };
export default FileDownloadResultContext;
